import WasmController from "react-lib/frameworks/WasmController";
// api
import BillingPYInterfaceList from "issara-sdk/apis/BillingPYInterfaceList_apps_INF";
import BillingPYInterfaceUpdateView from "issara-sdk/apis/BillingPYInterfaceUpdateView_apps_INF";
import UserList from "issara-sdk/apis/UserList_users";

import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";
import moment from "moment";

// Serializer
import BillingPYInterfaceSerializer from "issara-sdk/types/BillingPYInterfaceSerializer_apps_INF";

let toDate = moment();

export type Props = {

}

export type State = Partial<{
  // Sequence
  BillingPYInterfaceSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    searchBillingPY: any;
    billingPYList: any;
    userOptions: any;
    paginatedItems: { items?: any[]; page?: number; pageSize?: number; total?: number };
  }>;

  buttonLoadCheck?: any;
  errorMessage?: any;
  successMessage?: any;
}>;

export const StateInitial: State = {
  // Sequence
  BillingPYInterfaceSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  errorMessage: null,
  successMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export type TableHeaderType<T> = {
  Header: string;
  accessor: keyof T;
  width?: number;
};

export type AccessorType = Partial<Record<keyof BillingPYInterfaceSerializer, string>>;

export type WidthsType = Partial<Record<keyof BillingPYInterfaceSerializer, number>>;

export const LIST_LIMIT = 20;

const PAGINATED_INIT = {
  items: [],
  page: 0,
  total: 0,
  pageSize: LIST_LIMIT,
};

type Handler = (controller: WasmController<State, Event, Data, Props>, params?: any) => any;
/* ------------------------------------------------------ */
/*                          START                         */
/* ------------------------------------------------------ */
export const Start: Handler = async (controller, params) => {
  let state = controller.getState();

  controller.setState(
    {
      BillingPYInterfaceSequence: {
        ...state.BillingPYInterfaceSequence,
        sequenceIndex: "Action",
        searchBillingPY: {
          createDate: dateToStringWithoutTimeBE(toDate),
        },
        paginatedItems: { pageSize: LIST_LIMIT, page: 0 },
      },
    },
    // () => Action(controller, { action: "search" })
  );
};

/* ------------------------------------------------------ */
/*                         ACTION                         */
/* ------------------------------------------------------ */
export const Action: Handler = async (controller, params) => {
  if (params.action === "search") {
    HandleSearchData(controller, params);
  } else if (params.action === "clear") {
    HandleClearData(controller, params);
  } else if (params.action === "save") {
    HandleSaveData(controller, params);
  }
};

const HandleSearchData: Handler = async (controller, params) => {
  const state = controller.getState();
  const searchBillingPY = state.BillingPYInterfaceSequence?.searchBillingPY;
  const paginatedItems = state.BillingPYInterfaceSequence?.paginatedItems;

  const page = (params?.page ?? paginatedItems?.page) || 0;
  const pageSize = (params?.pageSize ?? paginatedItems?.pageSize) || LIST_LIMIT;

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.cardKey}_search`]: "LOADING",
    },
  });

  const offset = page * pageSize;

  let data = {
    limit: pageSize,
    offset,
    create_date: searchBillingPY?.createDate,
    hn: searchBillingPY?.hn,
    receipt_no: searchBillingPY?.receiptNo,
    payment_no: searchBillingPY?.paymentNo,
    payment_id: searchBillingPY?.paymentId,
  };

  const [billingPYListRes, billingPYListErr, billingPYListNet] = await BillingPYInterfaceList.list({
    apiToken: controller.apiToken,
    params: data,
  });

  if (billingPYListRes) {
    const uniqueData = (billingPYListRes?.items || [])
      .filter(
        (item: any, index: number, self: any) =>
          index === self.findIndex((t: any) => t.created_by === item.created_by)
      )
      .map((item: any) => item?.created_by);

    const userOptions = await Promise.all(
      uniqueData.map(async (id: number) => {
        const [userRes, userErr, userNet] = await UserList.list({
          apiToken: controller.apiToken,
          params: { pk: id },
        });

        return userRes?.items?.[0];
      })
    );

    controller.setState({
      BillingPYInterfaceSequence: {
        ...state.BillingPYInterfaceSequence,
        billingPYList: billingPYListRes?.items,
        userOptions: userOptions,
        paginatedItems: { 
          ...paginatedItems,
          page,
          pageSize,
          total: billingPYListRes?.total
        },
      },
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_search`]: "SUCCESS",
      },
    });
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_search`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [params.cardKey]: billingPYListErr,
      },
    });
  }
};

const HandleClearData: Handler = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    BillingPYInterfaceSequence: {
      ...state.BillingPYInterfaceSequence,
      searchBillingPY: {
        createDate: "",
        hn: "",
        receiptNo: "",
        paymentNo: "",
        paymentId: "",
      },
      billingPYList: [],
      paginatedItems: { ...PAGINATED_INIT },
    },
  });
};

const HandleSaveData: Handler = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: {
      ...state.buttonLoadCheck,
      [`${params.cardKey}_${params.action}`]: "LOADING",
    },
  });

  const apiType =
    params.type === "add"
      ? BillingPYInterfaceUpdateView.post
      : BillingPYInterfaceUpdateView.put;

  const [billingPYRes, billingPYErr, billingPYNet] = await apiType({
    apiToken: controller.apiToken,
    data: {
      item_id_list: params?.selectedPY || [],
    },
  });

  if (billingPYRes) {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_${params.type}`]: "SUCCESS",
      },
      successMessage: {
        ...state.successMessage,
        [params.cardKey]: "The system has successfully",
      }
    });

    HandleSearchData(controller, params);
  } else {
    controller.setState({
      buttonLoadCheck: {
        ...state.buttonLoadCheck,
        [`${params.cardKey}_${params.type}`]: "ERROR",
      },
      errorMessage: {
        ...state.errorMessage,
        [params.cardKey]: billingPYErr
      }
    });
  }
};

/* ------------------------------------------------------ */
/*                         UTILS                          */
/* ------------------------------------------------------ */

export const accessorList: Partial<Record<keyof BillingPYInterfaceSerializer, string>> = {
  id: "ID",
  valid: "Valid",
  error_message: "Error Message",
  created_by: "User ที่สร้าง",
  created_at: "วันเวลาที่สร้าง",
  sent: "Sent",
  file_name: "File Name",
  version: "Version",
  record_type: "Record Type",
  receipt_no: "Receipt No",
  payment_type: "Paymennt Type",
  bank: "Bank",
  payment_no: "Payment No",
  value: "Value",
  payment: "Payment",
};

export const GenerateTableHeader = (
  columnnWidths?: WidthsType
): TableHeaderType<BillingPYInterfaceSerializer>[] => {
  return (Object.entries(accessorList) as [keyof BillingPYInterfaceSerializer, string][]).map(
    ([key, label]) => ({
      Header: label,
      accessor: key,
      ...(columnnWidths && columnnWidths[key] ? { width: columnnWidths[key] } : {}),
    })
  );
};